import { CommonModule } from '@angular/common';
import { Component, Inject, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MultiLevelContextMenuItem } from 'src/app/components/api-configurations/common/models/multi-level-context-menu-item';
import { MultiLevelContextMenuComponent } from 'src/app/components/api-configurations/common/multi-level-context-menu/multi-level-context-menu.component';
import { ComparaisonOperator } from 'src/app/models/RestApiCaller/Enums/comparaison-operator';
import { DataType } from 'src/app/models/RestApiCaller/Enums/data-type';
import { RestApiConditionType } from 'src/app/models/RestApiCaller/Enums/rest-api-condition-type';
import { HttpCodeConditionDatas } from 'src/app/models/RestApiCaller/conditions/datas/http-code-condition-datas';
import { LogicalConditionDatas } from 'src/app/models/RestApiCaller/conditions/datas/logical-condition-datas';
import { VariableApiConditionDatas } from 'src/app/models/RestApiCaller/conditions/datas/variable-api-condition-datas';
import { RestApiCondition } from 'src/app/models/RestApiCaller/conditions/rest-api-condition';
import { ContextMenuItemEvent } from '../../../../common/models/context-menu-item-event';
import { ConditionFormDialogData } from '../Models/condition-form-dialog-data';
import { ConditionFormDialogResult } from '../Models/condition-form-dialog-result';

@Component({
    selector: 'app-condition-form-dialog',
    imports: [
        MatCardModule,
        FormsModule,
        MatFormFieldModule,
        MatButtonModule,
        MatInputModule,
        MatSelectModule,
        MatOptionModule,
        MultiLevelContextMenuComponent,
        CommonModule,
        ReactiveFormsModule,
    ],
    templateUrl: './condition-form-dialog.component.html',
    styleUrl: './condition-form-dialog.component.scss'
})
export class ConditionFormDialogComponent {
    apiConditionType = RestApiConditionType;
    public item: RestApiCondition;
    public title: string;

    public conditionsType: { value: string; name: string }[] = [
        { value: RestApiConditionType.HttpCode, name: 'HttpCode' },
        { value: RestApiConditionType.Variable, name: 'Variable' },
        { value: RestApiConditionType.Logical, name: 'Expression logique' },
    ];

    public comparaisons: { value: string; name: string }[] = [
        { value: ComparaisonOperator.Equality, name: 'Egal à' },
        { value: ComparaisonOperator.GreaterOrEqualThan, name: 'Supériueur ou égal à' },
        { value: ComparaisonOperator.GreaterThan, name: 'Supérieur à' },
        { value: ComparaisonOperator.Inequality, name: 'Différent de' },
        { value: ComparaisonOperator.LessOrEqualThan, name: 'Inférieur ou égal à' },
        { value: ComparaisonOperator.LessThan, name: 'Inférieur à' },
    ];

    public datasType: { value: string; name: string }[] = [
        { value: DataType.Text, name: 'Texte' },
        { value: DataType.Integer, name: 'Nombre entier' },
        { value: DataType.Decimal, name: 'Nombre décimal' },
        { value: DataType.Date, name: 'Date' },
    ];

    public responseVariables: { id: string; name: string }[] = [];

    public referenceConditions: RestApiCondition[] = [];

    /**
     * Composant du menu contextuel
     */
    @ViewChild(MultiLevelContextMenuComponent)
    contextMenu: MultiLevelContextMenuComponent;

    /**
     * Liste des éléments du menu contextuel.
     */
    contextMenuItems: MultiLevelContextMenuItem = null;

    /**
     * Index du début du texte sélectionné dans le champ (utilisé pour le placement d'une variable avec le menu contextuel).
     */
    currentSelectionStart: number;

    /**
     * Index de fin du texte sélectionné dans le champ sujet (utilisé pour le placement d'une variable avec le menu contextuel).
     */
    currentSelectionEnd: number;

    /**
     * Position horizontale de la sourris au moment d'un clic droit (utilisé pour positionner le menu contextuel)
     */
    rightClickMenuPositionX: number;

    /**
     * Position verticale de la sourris au moment d'un clic droit (utilisé pour positionner le menu contextuel)
     */
    rightClickMenuPositionY: number;

    /**
     * Valeur indiquant si le menu contextuel est visible.
     */
    isContextMenuVisible: boolean;

    apiConfigurationConditionForm: FormGroup;

    constructor(
        public dialogRef: MatDialogRef<ConditionFormDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ConditionFormDialogData
    ) {
        this.title = data.title;
        //this.contextMenuItems = data.contextMenuItems;
        this.item = RestApiCondition.clone(data.item);
        this.responseVariables = data.variables.map((x) => {
            return { id: x.name, name: x.name };
        });

        let conditionMenuItems: MultiLevelContextMenuItem[] = data.referencedConditions.map((x) => {
            return new MultiLevelContextMenuItem(`${x.name}`, `{${x.name}}`);
        });

        this.contextMenuItems = new MultiLevelContextMenuItem('Root', null, conditionMenuItems);

        this.apiConfigurationConditionForm = new FormGroup({
            name: new FormControl(''),
            description: new FormControl(''),
            conditionType: new FormControl(''),
            httpCode: new FormControl(''),
            comparaisonOperator: new FormControl(''),
            variableName: new FormControl(''),
            dataType: new FormControl(''),
            value: new FormControl(''),
            expression: new FormControl(''),
        });

        if (data.readonly) {
            this.apiConfigurationConditionForm.disable();
        }

        this.fillForm(this.item);
    }

    onConfirm(): void {
        this.RetrieveValueFromForm();
        let result: ConditionFormDialogResult = { status: true, item: this.item };

        // Close the dialog, return true
        this.dialogRef.close(result);
    }

    onCancel(): void {
        let result: ConditionFormDialogResult = { status: false, item: this.item };
        // Close the dialog, return false
        this.dialogRef.close(result);
    }

    /**
     * Levé lorsque l'utilisateur fait un clic-droit sur un champ de saisie.
     * @param source : Identifiant du composant. Permet de déterminer dans quel composant la variable sera ajouté.
     * @param event : Événement souris.
     */
    public onInputRightClick(source: string, event: MouseEvent) {
        this.isContextMenuVisible = true;

        // Position de la souris utilisé pour placer le menu contextuel.
        this.rightClickMenuPositionX = event.clientX;
        this.rightClickMenuPositionY = event.clientY;

        // Récupère la sélection dans le champ Input. Le paramètre remplacera cette selection.
        this.currentSelectionStart = event.target['selectionStart'];
        this.currentSelectionEnd = event.target['selectionEnd'];

        this.contextMenu.openMenu(source, event);
    }

    /**
     * Ferme le menu contextuel.
     */
    public CloseContextMenu() {
        this.isContextMenuVisible = false;
    }

    /**
     * Levé lorsque l'utilisateur clique sur un élément du menu contextuel.
     * @param event : Événement clic du menu contextuel.
     */
    handleMenuItemClick(event: ContextMenuItemEvent) {
        let expressionControl = this.apiConfigurationConditionForm.get('expression');

        if (expressionControl.value) {
            expressionControl.setValue(expressionControl.value.substring(0, this.currentSelectionStart) + event.data + expressionControl.value.substring(this.currentSelectionEnd));
        } else {
            expressionControl.setValue(event.data);
        }

        this.isContextMenuVisible = false;
    }

    /**
     * Retourne la valeur du formulaire.
     */
    RetrieveValueFromForm() {
        this.item.name = this.apiConfigurationConditionForm.get('name').value;
        this.item.description = this.apiConfigurationConditionForm.get('description').value;
        this.item.type = this.apiConfigurationConditionForm.get('conditionType').value;

        switch (this.item.type) {
            case RestApiConditionType.HttpCode:
                let httpCodeDatas = new HttpCodeConditionDatas();
                httpCodeDatas.HttpCode = this.apiConfigurationConditionForm.get('httpCode').value;
                httpCodeDatas.Operator = this.apiConfigurationConditionForm.get('comparaisonOperator').value;
                this.item.httpCodeConditionDatas = httpCodeDatas;
                break;
            case RestApiConditionType.Variable:
                let variableDatas = new VariableApiConditionDatas();
                variableDatas.VariableName = this.apiConfigurationConditionForm.get('variableName').value;
                variableDatas.DataType = this.apiConfigurationConditionForm.get('dataType').value;
                variableDatas.Operator = this.apiConfigurationConditionForm.get('comparaisonOperator').value;
                variableDatas.Value = this.apiConfigurationConditionForm.get('value').value;
                this.item.variableApiConditionDatas = variableDatas;
                break;
            case RestApiConditionType.Logical:
                let logicalDatas = new LogicalConditionDatas();
                logicalDatas.Expression = this.convertExpressionFromForm(this.apiConfigurationConditionForm.get('expression').value);
                this.item.logicalConditionDatas = logicalDatas;
                break;
        }
    }

    /**
     * Remplit les champs du formulaire avec les données de la condition.
     */
    private fillForm(item: RestApiCondition) {
        this.apiConfigurationConditionForm.get('name').setValue(item.name);
        this.apiConfigurationConditionForm.get('description').setValue(item.description);
        this.apiConfigurationConditionForm.get('conditionType').setValue(item.type);

        switch (item.type) {
            case RestApiConditionType.HttpCode:
                this.apiConfigurationConditionForm.get('httpCode').setValue(item.httpCodeConditionDatas.HttpCode);
                this.apiConfigurationConditionForm.get('comparaisonOperator').setValue(item.httpCodeConditionDatas.Operator);
                break;
            case RestApiConditionType.Variable:
                this.apiConfigurationConditionForm.get('variableName').setValue(item.variableApiConditionDatas.VariableName);
                this.apiConfigurationConditionForm.get('dataType').setValue(item.variableApiConditionDatas.DataType);
                this.apiConfigurationConditionForm.get('comparaisonOperator').setValue(item.variableApiConditionDatas.Operator);
                this.apiConfigurationConditionForm.get('value').setValue(item.variableApiConditionDatas.Value);
                break;
            case RestApiConditionType.Logical:
                this.apiConfigurationConditionForm.get('expression').setValue(this.convertExpressionFromData(item.logicalConditionDatas.Expression));
                break;
        }
    }

    private convertExpressionFromForm(expression: string): string {
        let convertedExpression = `${expression}`;
        var regex = /{([\w\s\-_])+}/g;

        let match;

        while ((match = regex.exec(expression)) !== null) {
            var conditionName = match[0];
            console.log('conditionName', conditionName);
            var conditionReference = this.data.referencedConditions.find((x) => `{${x.name}}` === conditionName).id;
            console.log('conditionReference', conditionReference);
            convertedExpression = convertedExpression.replace(conditionName, `{C${conditionReference}}`);
        }

        return convertedExpression;
    }

    private convertExpressionFromData(expression: string): string {
        let convertedExpression = `${expression}`;
        var regex = /{(C\d+)+}/g;

        let match;

        while ((match = regex.exec(expression)) !== null) {
            var conditionReference = match[0];
            console.log('conditionReference', conditionReference);
            var conditionName = this.data.referencedConditions.find((x) => `{C${x.id}}` === conditionReference).name;
            console.log('conditionName', conditionName);
            convertedExpression = convertedExpression.replace(conditionReference, `{${conditionName}}`);
        }

        return convertedExpression;
    }
}
